/**
 * OnboardingGuard
 *
 * Renders the app and lays the onboarding survey over it as a modal dialog
 * until `preferences.onboardingCompleted === true`.
 *
 * Used to `<Navigate to="/setup">` instead. That route is gone: a separate
 * setup page read as a hurdle in front of the product, and completed users
 * could re-open it by URL. Now the user sees the real app behind the backdrop
 * from the first second, and when the preference flips the dialog closes over
 * an already-mounted page — no remount, no reload, no flicker.
 *
 * Mounted INSIDE ConsentGuard so it only runs after consent has been accepted
 * and the user actually has access to protected content.
 *
 * Only the OWNER of the active organization gets the survey (Issue API
 * 2026-10-15 `eingeladenes-mitglied-bekommt-die-unternehmens-umfrage…`, KW-005,
 * Entscheid Torsten Nr. 544): it asks about the company, and `submit` files
 * the answers under `session.activeOrganizationId`. A self-registered user owns
 * the organization core-backend creates at sign-up; an invited user has none
 * until the invitation is accepted and then holds the invitation's role — the
 * inviter's organization. Unknown role (loading, no active organization) means
 * no dialog: opening it late beats filing answers under someone else's company.
 */

import { LoadingSpinner, useAuth, usePreferences } from '@xhubio-saas/core-frontend/kernel'
import { useEffect, useReducer } from 'react'
import { trpc } from '@/trpc'
import { OnboardingDialog } from './OnboardingDialog'

interface OnboardingGuardProps {
  children: React.ReactNode
}

/**
 * The kernel's `preferences.get` query is neither user-scoped nor invalidated
 * on auth change: after a client-side sign-out → sign-in/register (SPA
 * navigation, same QueryClient), it serves the PREVIOUS account's preferences
 * for up to its staleTime. Real-world impact both ways: a brand-new user got
 * NO survey (previous account had onboardingCompleted=true — jakob@xhub.io,
 * 2026-08-06), and a completed user got the survey again (previous account had
 * not completed). The dialog therefore stays closed until preferences have
 * been re-fetched for the CURRENT user id at least once.
 *
 * The same holds for `organization.current` (one query key for every user):
 * its `userRole` decides the gate, so it is re-fetched together with the
 * preferences.
 *
 * Module-scoped so page navigations (the guard mounts per page) don't refetch
 * again for the same user.
 */
let verifiedForUser: string | null = null

export function OnboardingGuard({ children }: OnboardingGuardProps) {
  const { session } = useAuth()
  const userId = session?.user?.id ?? null
  const { preferences, isLoading, refetch } = usePreferences()
  // Same options as `useIsOrgOwner` (hooks/useMemberAdminRights) — one cache entry.
  const org = trpc.organization.current.useQuery(undefined, { staleTime: 5 * 60 * 1000 })
  const refetchOrg = org.refetch
  const [, rerender] = useReducer((x: number) => x + 1, 0)

  useEffect(() => {
    if (!userId || verifiedForUser === userId) return
    void Promise.all([refetch(), refetchOrg()]).then(() => {
      verifiedForUser = userId
      rerender()
    })
  }, [userId, refetch, refetchOrg])

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  // No welcome banner anymore: the landing page is /invoice-conversion, which
  // carries its own InvoiceConversionHint. Stacking both pushed the actual
  // playground below the fold, and the banner's quick-links only duplicated
  // the sidebar.
  const freshForThisUser = userId !== null && verifiedForUser === userId
  // Exactly 'owner', lower case — the string core-backend writes for the creator.
  const ownsActiveOrg = org.data?.userRole === 'owner'
  return (
    <>
      {children}
      <OnboardingDialog
        open={freshForThisUser && ownsActiveOrg && !preferences.onboardingCompleted}
      />
    </>
  )
}
